export type Diagnostic = {
  /** Deduplication key: each distinct problem is logged once per page load. */
  readonly key: string;
  readonly title: string;
  readonly details: Readonly<Record<string, string>>;
  readonly fix: readonly string[];
  readonly level?: 'error' | 'warn';
};

declare global {
  interface Window {
    anovaDiagnostics?: {
      problems: readonly Diagnostic[];
      print: () => void;
    };
  }
}

const logged = new Set<string>();
const collected: Diagnostic[] = [];

function formatDiagnostic(diagnostic: Diagnostic): string {
  const labels = Object.keys(diagnostic.details);
  const width = labels.reduce((max, label) => Math.max(max, label.length), 0);

  return [
    `[Anova] ${diagnostic.title}`,
    ...Object.entries(diagnostic.details).map(
      ([label, value]) => `  ${label.padEnd(width)} : ${value}`,
    ),
    '  how to fix:',
    ...diagnostic.fix.map((hint) => `    - ${hint}`),
  ].join('\n');
}

function exposeOnWindow(): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.anovaDiagnostics = {
    problems: collected,
    print: () => {
      if (collected.length === 0) {
        console.info('[Anova] No configuration or API problems detected.');
        return;
      }
      collected.forEach((diagnostic) => {
        console.info(formatDiagnostic(diagnostic));
      });
    },
  };
}

/**
 * Prints an actionable configuration/connectivity problem to the browser console and keeps it
 * in `window.anovaDiagnostics` so the whole list can be replayed after the page settled.
 */
export function logDiagnostic(diagnostic: Diagnostic): void {
  if (logged.has(diagnostic.key)) {
    return;
  }

  logged.add(diagnostic.key);
  collected.push(diagnostic);
  exposeOnWindow();

  const write = diagnostic.level === 'warn' ? console.warn : console.error;
  write(formatDiagnostic(diagnostic));
}

export function getDiagnostics(): readonly Diagnostic[] {
  return collected;
}

/** Test helper: clears logged/collected state between cases. */
export function resetDiagnostics(): void {
  logged.clear();
  collected.length = 0;
}
